<template>
  <div
    class="up-next-row"
    :class="{
      'up-next-row--on-block': onBlock,
      'up-next-row--highlighted': highlighted,
      'up-next-row--last': last,
    }"
    title="Double-click to bring this item to the block"
    @dblclick="emit('move')"
  >
    <span class="up-next-row__run">#{{ run }}</span>

    <span class="up-next-row__description">
      <span class="up-next-row__title">
        <span class="up-next-row__year">{{ year }}</span>{{ title }}
      </span>
      <Tag v-if="repRequired" severity="info" icon="pi pi-user" value="Rep" rounded />
    </span>

    <span class="up-next-row__bid">
      <span class="up-next-row__bid-amount">{{ highBid == null ? '—' : `$${highBid.toLocaleString()}` }}</span>
      <span v-if="badge" class="up-next-row__bid-badge">{{ badge }}</span>
    </span>

    <span class="up-next-row__status">
      <span v-if="status" class="up-next-row__status-pill" :class="`up-next-row__status-pill--${statusModifier}`">
        {{ status }}
      </span>
      <span v-else class="up-next-row__status-empty">—</span>
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import Tag from 'primevue/tag';

// One lot in the run list: run number, description, high bid, and where the
// item's bidding has got to.
//
// The status pill is drawn here rather than through a shared badge component,
// and that is deliberate: it used to be a `Tag` fed an `overrideStyle` object
// of raw colors, which is a consumer reaching through a component to paint it.
// The five outcome fills are this component's tokens now, and they
// deliberately match the ActionGrid button that produces each one — a clerk
// scanning the list should see the same green for Sell as the button they
// pressed.
defineOptions({ name: 'UpNextRow' });

const props = defineProps({
  run: { type: [String, Number], required: true },
  year: { type: [String, Number], default: null },
  title: { type: String, default: '' },
  // The lot needs a selling representative present before it can be sold.
  repRequired: { type: Boolean, default: false },
  // Null when nobody has bid — renders an em dash rather than $0.
  highBid: { type: Number, default: null },
  // The high bidder's badge, as the runtime spells it.
  badge: { type: String, default: null },
  // ASKING | SELLING | SOLD | NOT-SOLD | OFFER, or any other runtime state,
  // which falls through to a neutral pill.
  status: { type: String, default: null },
  // This lot is the one on the block.
  onBlock: { type: Boolean, default: false },
  // Momentarily called out — a lot just moved in from another event.
  highlighted: { type: Boolean, default: false },
  // Last row in the list; drops the divider.
  last: { type: Boolean, default: false },
});

const emit = defineEmits(['move']);

const STATUS_MODIFIERS = {
  ASKING: 'asking',
  SELLING: 'selling',
  SOLD: 'sold',
  'NOT-SOLD': 'not-sold',
  OFFER: 'offer',
};
const statusModifier = computed(() => STATUS_MODIFIERS[props.status] ?? 'unknown');
</script>

<style scoped>
.up-next-row {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  transition: background 120ms;
  gap: var(--p-up-next-row-gap);
  padding: var(--p-up-next-row-padding);
  border-bottom: var(--p-up-next-row-border-bottom-width) solid var(--p-up-next-row-border-bottom-color);
}

.up-next-row--last {
  border-bottom-width: 0;
}

/* Mint wash plus a left rail, matching the "current increment" treatment on the
   ladder — the two things that say "this is the lot in front of you" agree. */
.up-next-row--on-block,
.up-next-row--highlighted {
  background: var(--p-up-next-row-on-block-background);
}

.up-next-row--on-block {
  box-shadow: var(--p-up-next-row-on-block-box-shadow);
}

.up-next-row--on-block .up-next-row__run {
  background: var(--p-up-next-row-on-block-run-background);
}

.up-next-row__run {
  font-family: var(--p-up-next-row-run-font-family);
  font-weight: var(--p-up-next-row-run-font-weight);
  font-size: var(--p-up-next-row-run-font-size);
  color: var(--p-up-next-row-run-color);
  letter-spacing: var(--p-up-next-row-run-letter-spacing);
  padding: var(--p-up-next-row-run-padding);
  border-radius: var(--p-up-next-row-run-border-radius);
  background: var(--p-up-next-row-run-background);
}

.up-next-row__description {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: var(--p-up-next-row-description-gap);
}

.up-next-row__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  font-size: var(--p-up-next-row-title-font-size);
  font-weight: var(--p-up-next-row-title-font-weight);
  color: var(--p-up-next-row-title-color);
}

.up-next-row__year {
  margin-right: 6px;
  font-weight: var(--p-up-next-row-year-font-weight);
  color: var(--p-up-next-row-year-color);
}

.up-next-row__bid {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 84px;
  gap: var(--p-up-next-row-bid-gap);
}

.up-next-row__bid-amount {
  font-family: var(--p-up-next-row-bid-amount-font-family);
  font-size: var(--p-up-next-row-bid-amount-font-size);
  font-weight: var(--p-up-next-row-bid-amount-font-weight);
  color: var(--p-up-next-row-bid-amount-color);
  font-variant-numeric: tabular-nums;
}

.up-next-row__bid-badge {
  font-size: var(--p-up-next-row-bid-badge-font-size);
  color: var(--p-up-next-row-bid-badge-color);
}

.up-next-row__status {
  display: flex;
  justify-content: flex-end;
  min-width: 76px;
}

.up-next-row__status-empty {
  font-size: var(--p-up-next-row-status-empty-font-size);
  color: var(--p-up-next-row-status-empty-color);
}

/* ── Status pills ───────────────────────────────────────────────────────── */

.up-next-row__status-pill {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: var(--p-up-next-row-status-pill-padding);
  border-radius: var(--p-up-next-row-status-pill-border-radius);
  font-size: var(--p-up-next-row-status-pill-font-size);
  font-weight: var(--p-up-next-row-status-pill-font-weight);
  letter-spacing: var(--p-up-next-row-status-pill-letter-spacing);
}

.up-next-row__status-pill--asking {
  background: var(--p-up-next-row-status-pill-asking-background);
  color: var(--p-up-next-row-status-pill-asking-color);
}

.up-next-row__status-pill--selling {
  background: var(--p-up-next-row-status-pill-selling-background);
  color: var(--p-up-next-row-status-pill-selling-color);
}

.up-next-row__status-pill--sold {
  background: var(--p-up-next-row-status-pill-sold-background);
  color: var(--p-up-next-row-status-pill-sold-color);
}

.up-next-row__status-pill--not-sold {
  background: var(--p-up-next-row-status-pill-not-sold-background);
  color: var(--p-up-next-row-status-pill-not-sold-color);
}

.up-next-row__status-pill--offer {
  background: var(--p-up-next-row-status-pill-offer-background);
  color: var(--p-up-next-row-status-pill-offer-color);
}

.up-next-row__status-pill--unknown {
  background: var(--p-up-next-row-status-pill-unknown-background);
  color: var(--p-up-next-row-status-pill-unknown-color);
}
</style>
